import { useGoogleMapStore } from '@store/googleMapStore';
import { ReactElement, useEffect, useRef, useState } from 'react';

declare global {
	interface Window {
		google: typeof google;
		initMap: () => void;
	}
}

interface IGoogleMap {
	setAddress: (val: string) => void;
	setDetailedAddress?: (val: { longitude: string; latitude: string; pincode: string; city: string }) => void;
	/** When set (e.g. existing site coords), center the marker here instead of using browser geolocation. */
	initialLatitude?: number | null;
	initialLongitude?: number | null;
	/** Move marker when a place is picked from search without reverse-geocoding again. */
	focusLatitude?: number | null;
	focusLongitude?: number | null;
}

export const GoogleMap = (props: IGoogleMap): ReactElement => {
	const { setAddress, setDetailedAddress, initialLatitude, initialLongitude, focusLatitude, focusLongitude } = props;
	const mapDivRef = useRef<HTMLDivElement | null>(null);
	const mapInstanceRef = useRef<google.maps.Map | null>(null);
	const markerInstanceRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
	const { setCurrentAddress } = useGoogleMapStore();
	const lastZoomRef = useRef<number>(10);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!window.google?.maps || !mapDivRef.current) return;

		const initMap = (): void => {
			if (mapDivRef.current) {
				const map = new window.google.maps.Map(mapDivRef.current, {
					center: { lat: -34.397, lng: 150.644 },
					zoom: 15,
					mapId: 'SITE_LEAD_MAP',
				});
				mapInstanceRef.current = map;

				const marker = new google.maps.marker.AdvancedMarkerElement({
					map: map,
					gmpDraggable: true,
					content: new google.maps.marker.PinElement({
						scale: 0.8,
						background: '#EA4335',
						borderColor: '#B31412',
					}).element,
				});
				markerInstanceRef.current = marker;

				const getGeocode = (position: google.maps.LatLng | google.maps.LatLngLiteral): void => {
					const geocoder = new window.google.maps.Geocoder();
					geocoder.geocode({ location: position }, (results, status) => {
						if (status === 'OK' && results?.[0]) {
							const result = results[0];
							const formattedAddress = result.formatted_address;

							// Set the main address
							setAddress(formattedAddress);
							setCurrentAddress(formattedAddress);

							// If detailed address handler is provided, extract and set additional details
							if (setDetailedAddress) {
								// Get lat/lng values properly from the position object
								const lat = typeof position.lat === 'function' ? position.lat() : position.lat;
								const lng = typeof position.lng === 'function' ? position.lng() : position.lng;

								const detailedAddress = {
									longitude: lng.toString(),
									latitude: lat.toString(),
									pincode: '',
									city: '',
								};

								result.address_components.forEach((component) => {
									if (component.types.includes('postal_code')) {
										detailedAddress.pincode = component.long_name;
									}
									if (component.types.includes('locality')) {
										detailedAddress.city = component.long_name;
									}
								});

								setDetailedAddress(detailedAddress);
							}
						} else {
							setError('Failed to get address. Please try again.');
						}
					});
				};

				const setPositionAndGeocode = (position: google.maps.LatLng | google.maps.LatLngLiteral): void => {
					marker.position = position;
					map.setCenter(position);
					getGeocode(position);
				};

				const hasInitialCoords =
					initialLatitude != null &&
					initialLongitude != null &&
					Number.isFinite(initialLatitude) &&
					Number.isFinite(initialLongitude);

				const getUserLocation = (): void => {
					if (navigator.geolocation) {
						const requestGeolocation = (): void => {
							// geolocation is required here to center the map around the user's current position.
							// SonarJS: S5604 - This usage is intentional and complies with privacy requirements.
							navigator.geolocation.getCurrentPosition(
								(position) => {
									const pos = {
										lat: position.coords.latitude,
										lng: position.coords.longitude,
									};
									setPositionAndGeocode(pos);
									setError(null);
								},
								(error) => {
									if (error.code === error.PERMISSION_DENIED) {
										setError('Location access was denied. You can click on the map to set a location.');
									} else {
										setError('Failed to get your location. You can click on the map to set a location.');
									}
								},
								{ timeout: 10000, maximumAge: 60000 }
							);
						};
						requestGeolocation();
						// geolocation is required here to center the map around the user's current position.
						// SonarJS: S5604 - This usage is intentional and complies with privacy requirements.
						navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
							if (permissionStatus.state === 'prompt') {
								setError('Please allow location access when prompted to auto-capture your position.');
							}

							// Listen for changes to the permission state
							permissionStatus.onchange = (): void => {
								if (permissionStatus.state === 'granted') {
									requestGeolocation();
								} else if (permissionStatus.state === 'denied') {
									setError('Location access was denied. You can click on the map to set a location.');
								}
							};
						});
					} else {
						setError('Geolocation is not supported by this browser. You can click on the map to set a location.');
					}
				};

				if (hasInitialCoords && initialLatitude != null && initialLongitude != null) {
					setPositionAndGeocode({ lat: initialLatitude, lng: initialLongitude });
					setError(null);
				} else {
					getUserLocation();
				}

				map.addListener('click', (event: google.maps.MapMouseEvent) => {
					const clickedPos = event.latLng;
					if (clickedPos) {
						setPositionAndGeocode(clickedPos);
					}
				});

				marker.addListener('dragend', () => {
					const newPos = marker.position;
					if (newPos) {
						setPositionAndGeocode(newPos);
					}
				});

				map.addListener('zoom_changed', () => {
					const currentZoom = map.getZoom() ?? 0;
					const zoomDiff = currentZoom - lastZoomRef.current;

					if (Math.abs(zoomDiff) === 1) {
						map.setZoom(lastZoomRef.current + zoomDiff * 2);
					} else {
						lastZoomRef.current = currentZoom;
					}
				});
			}
		};

		if (window?.google?.maps) {
			initMap();
		} else {
			window.initMap = initMap;
		}
	}, [setAddress, setCurrentAddress, setDetailedAddress, initialLatitude, initialLongitude]);

	useEffect(() => {
		if (focusLatitude == null || focusLongitude == null) return;
		if (!Number.isFinite(focusLatitude) || !Number.isFinite(focusLongitude)) return;

		const map = mapInstanceRef.current;
		const marker = markerInstanceRef.current;
		if (!map || !marker) return;

		const position = { lat: focusLatitude, lng: focusLongitude };
		marker.position = position;
		map.setCenter(position);
		setError(null);
	}, [focusLatitude, focusLongitude]);

	return (
		<div>
			<div ref={mapDivRef} style={{ height: '100%', width: '100%' }}></div>
			{error && <p className={`error-message ${error && 'show'}`}>{error}</p>}
		</div>
	);
};

export default GoogleMap;
